import React, { useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import {
    useSetUserPasswordMutation,
    IUserSignupArgsType,
    useVerifyPasswordResetTokenQuery,
} from 'graphql/types';
import { SetPasswordForm } from 'auth/components/forms/SetPasswordForm';
import { useSnackbarContext } from 'common/context';
import { Link } from 'common/components/Link';
import { Loader } from 'common/components/Loader';
import { getUrlParamValue } from 'utils';
import { getIntlPath } from 'common/utils';

import { BaseAuthPage } from './BaseAuthPage';

export const SetPasswordPage: React.FC = () => {
    const { t } = useTranslation();

    const [submitted, setSubmitted] = useState(false);
    const { setSnackbarState } = useSnackbarContext();
    const uid = getUrlParamValue('uid') ?? '';
    const token = getUrlParamValue('token') ?? '';

    const { loading } = useVerifyPasswordResetTokenQuery({
        variables: {
            uid,
            token,
        },
        onError: () => {
            setSnackbarState({
                message: t('messages.passwordResetLinkExpired'),
                variant: 'error',
            });
        },
    });

    const [mutatePassword] = useSetUserPasswordMutation({
        update: () => {
            setSnackbarState({
                message: t('messages.passwordSet'),
                variant: 'success',
            });

            setSubmitted(true);
        },
        onError: (error) => {
            setSnackbarState({
                message: error.message,
                variant: 'error',
            });
        },
    });

    const handleSubmit = (values: IUserSignupArgsType) => {
        mutatePassword({
            variables: {
                uid,
                token,
                userData: {
                    password: values.password,
                },
            },
        });
    };

    return (
        <BaseAuthPage>
            {loading && <Loader />}

            {!loading && submitted && (
                <Box width="100%">
                    <Typography color="primary" variant="h2">
                        {t('account.setPassword.success')}
                    </Typography>

                    <Typography
                        color="textSecondary"
                        component="span"
                        variant="body2"
                    >
                        {t('account.setPassword.login_1')}
                    </Typography>

                    <Link to={getIntlPath('login')}>
                        {t('account.setPassword.login_2')}
                    </Link>
                </Box>
            )}

            {!loading && !submitted && (
                <SetPasswordForm onSubmit={handleSubmit} />
            )}
        </BaseAuthPage>
    );
};
